import { useAuthorization } from "@/hooks/use-authorization";
import ExtensionModal from "@/pages/extension/modal";
import { useGetApprovalGroup } from "@/queries/approval-groups";
import { useGetExtensionRequests } from "@/queries/extension";
import { useGetMe } from "@/queries/me";
import type { ExtensionRequestType } from "@/types/entities/extension";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import { formatDateToCommaString, formatToRelativeTime } from "@/utils/dates";
import { Permissions } from "@/utils/permissions";
import { Badge, Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  SUSPENSION_REQUEST_DATA_PARAMS,
  useGetSuspensionRequests,
  type SuspensionRequest,
} from "@/queries/deadline-suspensions/deadline-suspensions.api";
import ApproveSuspensionModal from "./approve-suspension";
import { useAuth } from "@/contexts/AuthProvider";

interface Props {
  data: FormalityRequestType;
  isLoading?: boolean;
  isCancelled?: boolean;
}

/**
 * Mapping of formality request status values to user-friendly labels
 */
const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  DRAFT: "Borrador",
  SENT: "Enviado",
  IN_PROCESS: "En proceso",
  IN_CORRECTION: "En revisión",
  IN_LEADER_REVIEW: "Para visto bueno",
  WITH_CORRECTIONS: "Observado",
  UNDER_REVIEW: "Revisando corrección",
  EXTENSION_REQUEST: "Solicitud de prórroga",
  SUSPENSION_REQUEST: "Solicitud suspensión",
  ON_EXTENSION: "En prórroga",
  WITH_SUSPENSION: "Suspendido",
  PAYMENT_PENDING: "Pendiente de pago",
  APPROVED: "Aprobado",
  REJECTED: "Denegado",
  UNFAIR: "Improcedente",
  CANCELED: "Cancelado",
  TIME_EXPIRED: "Vencido",
  CAN_EXTENDED: "Próroga disponible",
  FORCE_COMPLETED: "Finalizado por sistema",
  COMPLETED: "Finalizado",
  EXTERNAL_FORM_PENDING_ENTRY: "Pendiente de ingreso",
  EXTERNAL_FORM_RECEIVED: "Ingresado",
};

/**
 * Procedure general information component
 */
export const ProcedureInfo = ({ data, isLoading, isCancelled }: Props) => {
  const navigate = useNavigate();
  const { getUserRoleType } = useAuth();
  const { hasPermission } = useAuthorization();
  const canReadExtensionRequests = hasPermission(
    Permissions["node-time-extension-requests"].read,
  );
  const canUpdateExtensionRequests = hasPermission(
    Permissions["node-time-extension-requests"].update,
  );
  const canUpdateSuspensionRequests = hasPermission(
    Permissions["node-deadline-suspensions"].update,
  );
  const { data: me } = useGetMe();
  const params = useParams<{ procedureId: string }>();
  const [modalState, setModalState] = useState<
    "idle" | "request" | "suspension"
  >("idle");
  const [currentExtensionRequest, setCurrentExtensionRequest] =
    useState<ExtensionRequestType | null>(null);
  const [currentSuspensionRequest, setCurrentSuspensionRequest] =
    useState<SuspensionRequest | null>(null);
  const [shouldOpenOnLoad, setShouldOpenOnLoad] = useState(true);
  const [shouldOpenSROnLoad, setShouldOpenSROnLoad] = useState(true);

  const group = useMemo(() => {
    if (!data) return null;
    if (!data.groups) return null;
    return data.groups[data.groups.length - 1];
  }, [data]);
  const { data: groupData } = useGetApprovalGroup(group?.id || "");

  const isUserLeaderInThisGroup = useMemo(() => {
    if (!me || !data || !data.groups) return false;
    const grupsWhereUserIsLeader = me.data.groups.filter((myGroup) =>
      data.groups.some((group) => group.id === myGroup.id),
    );
    return grupsWhereUserIsLeader.some((g) => g.pivot?.seniority === 1);
  }, [me, data]);

  const { data: extensions, refetch } = useGetExtensionRequests(
    data?.id,
    {
      status: isUserLeaderInThisGroup ? "SCALED" : "PENDING",
      ...(isUserLeaderInThisGroup ? { scaled_to: me?.data.id } : {}),
    },
    !!me && canReadExtensionRequests,
  );
  const { data: suspensionRequest, refetch: refetchSuspension } =
    useGetSuspensionRequests(
      params.procedureId ?? "",
      0,
      SUSPENSION_REQUEST_DATA_PARAMS,
    );

  useEffect(() => {
    if (extensions && extensions.data.length > 0) {
      const extension = extensions.data.find((ext) => {
        if (ext.formality_request_id !== params.procedureId!) return false;

        // Only show if the request hasn't been processed yet
        return (
          (!ext.approved_at && !ext.denied_at && !ext.scaled_at) ||
          // Or if it's scaled to the current user but not approved/denied yet
          (!ext.approved_at && !ext.denied_at && ext.scaled_to === me?.data.id)
        );
      });

      if (extension) {
        setCurrentExtensionRequest(extension);
        if (shouldOpenOnLoad) {
          setModalState("request");
          setShouldOpenOnLoad(false);
        }
      } else {
        setCurrentExtensionRequest(null);
      }
    }
    return () => {
      setCurrentExtensionRequest(null);
    };
  }, [extensions, params, shouldOpenOnLoad, me]);

  useEffect(() => {
    const suspensionRequests = suspensionRequest?.data?.requests;
    if (suspensionRequests && suspensionRequests.length > 0) {
      const suspension = suspensionRequests.find((s) => {
        return (
          (!s.approved_at && !s.denied_at && !s.scaled_at) ||
          // check if the suspension is scalated to the owner but not approved yet
          (!s.approved_at && s.scaled_to === me?.data.id)
        );
      });

      if (suspension) {
        setCurrentSuspensionRequest(suspension);
        if (shouldOpenSROnLoad) {
          setModalState("suspension");
          setShouldOpenSROnLoad(false);
        }
      } else {
        setCurrentSuspensionRequest(null);
      }
    }
    return () => {
      setCurrentSuspensionRequest(null);
    };
  }, [
    suspensionRequest,
    params,
    shouldOpenSROnLoad,
    isUserLeaderInThisGroup,
    me,
  ]);

  const roleType = getUserRoleType();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 w-full laptop:w-[320px] items-center justify-center">
        <Icon
          name="spinner-third"
          type="fas"
          className="animate-spin text-body-quiet"
        />
      </div>
    );
  }

  return (
    <>
      {currentExtensionRequest &&
        modalState === "request" &&
        !isCancelled &&
        canReadExtensionRequests && (
          <ExtensionModal
            isOpen={modalState === "request"}
            onClose={() => {
              setModalState("idle");
              refetch();
            }}
            canSendToLead={!!group?.id && !isUserLeaderInThisGroup}
            id={currentExtensionRequest?.id || ""}
            data={currentExtensionRequest}
            group={groupData}
            canAction={canUpdateExtensionRequests}
          />
        )}
      {currentSuspensionRequest &&
        modalState === "suspension" &&
        !isCancelled && (
          <ApproveSuspensionModal
            data={currentSuspensionRequest}
            isOpen={modalState === "suspension"}
            onClose={() => {
              setModalState("idle");
              refetchSuspension();
            }}
            canAction={canUpdateSuspensionRequests}
            max_days_allowed={suspensionRequest?.data?.max_days}
            group={groupData}
            canSendToLead={!!group?.id && !isUserLeaderInThisGroup}
          />
        )}
      <div className="flex flex-col gap-4 w-full min-w-[320px] xl:w-[320px] xl:shrink-0">
        <div className="flex flex-col gap-2">
          <Heading variant="h6">Información general</Heading>
          <p
            className="text-body rich-text-content"
            dangerouslySetInnerHTML={{
              __html: data?.process?.description || "",
            }}
          />
          {roleType === "admin" && (
            <div className="flex flex-col gap-2 items-start">
              <Button
                variant="quiet"
                size="xs"
                onClick={() => {
                  navigate(`/procesos/${data?.process?.id}`);
                }}
              >
                Ir al proceso asociado <Icon type="far" name="arrow-up-right" />
              </Button>
              <Button
                variant="quiet"
                size="xs"
                onClick={() => {
                  navigate(
                    `/procesos/${data?.process?.id}/flujo/${data?.flowExecution?.flow_id}/ejecuciones?execution_id=${data?.flow_execution_id}`,
                  );
                }}
              >
                Ir a la ejecución <Icon type="far" name="arrow-up-right" />
              </Button>
            </div>
          )}
        </div>
        <hr className="border-neutral-quiet border" />
        <div>
          <p className="text-size-body-sm font-bold text-body-loud uppercase">
            Número de solicitud: <br />
            <span className="font-geist-mono text-body text-size-body font-normal">
              {data?.code?.toUpperCase() || ""}
            </span>
          </p>
        </div>
        <hr className="border-neutral-quiet border" />
        <div>
          <p className="text-body">
            Iniciado el {formatDateToCommaString(data?.created_at || "")} por{" "}
            <span className="text-action">
              {`${data?.applicant?.first_name || ""} ${data?.applicant?.last_name || ""}`.trim() ||
                "-"}
            </span>
          </p>
          {data?.assignedTo && (
            <p className="text-body">
              Responsable{" "}
              <span className="text-action">
                {data?.assignedTo.name} {data?.assignedTo.last_name}
              </span>
            </p>
          )}
          <p className="text-body">
            Última actualización {formatToRelativeTime(data?.updated_at || "")}
          </p>
        </div>
        <hr className="border-neutral-quiet border" />
        <div className="flex flex-col gap-3">
          {(data?.process?.tags?.length || 0) > 0 && (
            <div className="flex flex-row items-center gap-2">
              <Badge
                variant="neutral"
                shape="square"
                size="sm"
                className="h-6 p-0 min-w-[24px] justify-center flex rounded-lg"
              >
                <Icon
                  className="text-body-quiet"
                  size="sm"
                  type="fas"
                  name="tag"
                />
              </Badge>
              <span className="text-body">
                {data?.process?.tags?.map((tag) => tag.name).join(", ") || ""}
              </span>
            </div>
          )}
          {data?.status && (
            <div className="flex flex-row items-center gap-2">
              <Badge
                variant="info"
                shape="square"
                size="sm"
                className="h-6 p-0 min-w-[24px] justify-center flex rounded-lg"
              >
                <Icon
                  className="text-info-quiet "
                  size="sm"
                  type="fas"
                  name="file-pen"
                />
              </Badge>
              <span className="text-info">
                {data?.status
                  ? data.status_label ||
                    STATUS_LABELS[data.status] ||
                    data.status
                  : ""}
              </span>
            </div>
          )}
          {/* <div className="flex flex-row items-center gap-2">
          <Badge
            variant="neutral"
            shape="square"
            size="sm"
            className="h-6 p-0 min-w-[24px] justify-center flex rounded-lg"
          >
            <Icon
              className="text-body-quiet"
              size="sm"
              type="fas"
              name="circle-nodes"
            />
          </Badge>
          <span className="text-body">Nombre de la modalidad</span>
        </div> */}
          {/* <div className="flex flex-row items-center gap-2">
          <Badge
            variant="warning"
            shape="square"
            size="sm"
            className="h-6 p-0 min-w-[24px] justify-center flex rounded-lg"
          >
            <Icon
              className="text-warning-quiet"
              size="sm"
              type="fas"
              name="dollar"
            />
          </Badge>
          <span className="text-warning">Pendiente de pago</span>
        </div> */}
        </div>
        {/* <hr className="border-neutral-quiet border" />
      <div className="flex flex-col gap-1">
        <Heading variant="h6">Acciones</Heading>
        <Progress value={60} aria-label="Upload progress" />
        <p className="text-body-quiet">13 de 24 pasos realizados</p>
      </div> */}
        {(currentExtensionRequest || currentSuspensionRequest) && (
          <>
            <hr className="border-neutral-quiet border" />
            <div>
              <Button
                variant="warning-secondary"
                className="w-auto gap-2 disabled:bg-neutral-object-accent-silent disabled:text-on-disabled disabled:border-neutral-silent"
                size="sm"
                type="button"
                disabled={isCancelled}
                onClick={() => {
                  if (currentExtensionRequest) {
                    setModalState("request");
                    return;
                  }
                  setModalState("suspension");
                }}
              >
                <Icon type="far" name="timer" />
                Responder solicitud de plazo
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};
